"use client";
import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import CloseIcon from "@/assets/icons/close-thin.svg";
import { Button } from "@/app/(components)/Basic/Button";
import useWalletSignedFunction from "@/shared/hooks/useWalletSignedFunction";
import { OfferWithNft } from "../../../User/OffersTable/types";
import { Collection } from "@/api/collections/types";
import { useSuccessModal } from "@/app/(components)/Basic/SuccessModal/context/context";
import useOrdersInvalidation from "@/shared/hooks/useOrdersInvalidation";
import useCancelOrderNonce from "@/web3/contracts/Exchange/actions/useCancelOrderNonce";
import { ConfirmModal } from "@/app/(components)/Basic/ConfirmModal";

export interface Props {
  offer: OfferWithNft;
  collection?: Collection;
  title?: string;
  className?: string;
  disable?: boolean;
}

export default function CancelOfferButton({
  offer,
  collection,
  className,
  disable,
  title = "Cancel",
}: Props) {
  const { setSuccessData } = useSuccessModal();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const invalidateOrders = useOrdersInvalidation(offer.nft?.id);

  const cancelOrderNonce = useCancelOrderNonce();

  const item = useMemo(() => {
    if (!offer.isForCollection) {
      return {
        name: offer.nft!.name,
        media: offer.nft!.thumbnail!,
        mediaHeight: offer.nft!.imageHeight!,
        mediaWidth: offer.nft!.imageWidth!,
        placeholder: offer.nft!.placeholder,
      };
    }

    return {
      name: collection!.name,
      media: collection!.imageUrl,
    };
  }, [collection, offer.isForCollection, offer.nft]);

  const onClose = () => {
    setIsCancelModalOpen(false);
  };

  const { mutateAsync: cancelOffer, isLoading } = useMutation({
    mutationFn: cancelOrderNonce,
    onSuccess: async () => {
      invalidateOrders();

      await setSuccessData({
        item,
        title: "Cancel offer",
        text: "Your offer is successfully canceled",
      });
      onClose();
    },
  });

  const checkedCancelOffer = useWalletSignedFunction(() =>
    setIsCancelModalOpen(true),
  );

  return (
    <>
      <Button
        onClick={checkedCancelOffer}
        className={className}
        startIcon={<CloseIcon fill="primary endemic:invert" />}
        endIcon={<ArrowRightIcon className="w-4" />}
        disabled={disable || isLoading}
        disableClick={isLoading}
      >
        {!isLoading ? title : "Loading"}
      </Button>
      <ConfirmModal
        isOpen={isCancelModalOpen}
        isLoading={isLoading}
        onSubmit={() => cancelOffer(offer.orderNonce)}
        onClose={onClose}
        title={
          offer.isForCollection
            ? "Cancel collection offer"
            : "Cancel artwork offer"
        }
        text={
          offer.isForCollection
            ? "Are you sure you want to cancel this collection offer?"
            : "Are you sure you want to cancel this artwork offer?"
        }
      />
    </>
  );
}
